/**
 * Shared Subscription & Payment surface.
 *
 * ONE reusable surface for the "Stripe subscription payment integration" epic,
 * consumed by all three platforms (recruiter / training / agent) — NOT three
 * separate pages. This slice (#2551) is the scaffold: it is platform-aware,
 * renders the current subscription, and shows clearly-labelled placeholders for
 * the "Payment method" (card collection, #2552) and "Plans" (subscribe, #2553)
 * slices, so it is shippable on its own behind VITE_STRIPE_SUBSCRIPTION_ENABLED.
 *
 * Subscriptions are company-scoped, so this page fetches the current
 * subscription directly via getSubscriptionActive() — that works identically on
 * every platform (the SubscriptionProvider context only fetches for HR today).
 */
import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { ActivityIcon, CreditCardIcon, FileTextIcon } from "lucide-react";

import AuthLayout from "@container/layout/auth";
import TrainingAuthLayout from "@container/layout/training-auth";
import BillingPaymentDetails from "@container/billing-payment-details";
import BillingWorkspaceActivity from "@container/billing-workspace-activity";
import BillingInvoice from "@container/billing-invoice";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import { useUserProfileContext } from "@context/user_profile";
import {
    BillingPlatform,
    billingPlatformLabel,
    resolveBillingPlatform,
    STRIPE_SUBSCRIPTION_ENABLED,
} from "@util/billing-platform";

// Wraps the surface in the platform's own layout/sidebar so it sits inside the
// correct chrome for each platform. Training has its own layout; recruiter and
// agent both use AuthLayout.
function PlatformLayout({
    platform,
    children,
}: {
    platform: BillingPlatform;
    children: React.ReactNode;
}) {
    if (platform === "training") {
        return <TrainingAuthLayout>{children}</TrainingAuthLayout>;
    }
    return <AuthLayout>{children}</AuthLayout>;
}

export default function SubscriptionPaymentPage() {
    const [isLoadingUser, userInfo] = useUserProfileContext();
    const platform = useMemo(() => resolveBillingPlatform(userInfo), [userInfo]);

    // Flag OFF (default) ⇒ the surface does not exist; bounce to dashboard so the
    // route is a no-op until the epic is switched on.
    if (!STRIPE_SUBSCRIPTION_ENABLED) {
        return <Navigate to="/dashboard" replace />;
    }

    // Wait for the profile before resolving the platform, otherwise we'd flash
    // the unauthorized redirect for a logged-in user mid-load.
    if (isLoadingUser) {
        return null;
    }

    // No subscription surface for this user's platform (e.g. job_seeker).
    if (!platform) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <PlatformLayout platform={platform}>
            <div className="space-y-6 pb-16">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                        Subscription &amp; Payment
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Manage your {billingPlatformLabel(platform)} subscription and
                        payment method.
                    </p>
                </div>

                <Tabs defaultValue="payment" className="space-y-6">
                    {/* Underline tab style — matches the Billing menu tabs
                        (Hiring Activity / Plan Overview / Invoices). */}
                    <TabsList className="flex w-auto bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none">
                        <TabsTrigger
                            value="payment"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                        >
                            <CreditCardIcon className="h-4 w-4" />
                            Payment Details
                        </TabsTrigger>
                        <TabsTrigger
                            value="activity"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                        >
                            <ActivityIcon className="h-4 w-4" />
                            Workspace Activity
                        </TabsTrigger>
                        <TabsTrigger
                            value="invoices"
                            className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                        >
                            <FileTextIcon className="h-4 w-4" />
                            Invoices
                        </TabsTrigger>
                    </TabsList>

                    {/* ── Payment Details — shared self-contained surface, also
                        rendered on the Billing menu's "Plan Overview" tab ── */}
                    <TabsContent value="payment">
                        <BillingPaymentDetails />
                    </TabsContent>

                    {/* ── Workspace Activity — shared platform-aware surface, also
                        rendered on the Billing menu's "Workspace Activity" tab ── */}
                    <TabsContent value="activity">
                        {/* Hide the Current Billing Period card here — the Payment
                            Details tab already shows the current subscription. */}
                        <BillingWorkspaceActivity platform={platform} showBillingPeriod={false} />
                    </TabsContent>

                    {/* ── Invoices — moved here from the Billing menu. Self-contained
                        (fetches its own invoices); no request-info dialog on this
                        surface, so the optional prop is omitted. ── */}
                    <TabsContent value="invoices">
                        <BillingInvoice />
                    </TabsContent>
                </Tabs>
            </div>
        </PlatformLayout>
    );
}
